import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { getCookieDomain, isomorphicCookies } from 'shared/storage'

import { useDeusExMachina } from '~/domains/platform/infra/deus-ex-machina/hocs/with-deus-ex-machina'
import { Bucket } from '~/domains/platform/lib/bucket'
import {
  ACTIVE_PAYMENT_PROFILE_BUCKET_KEY as BUCKET_KEY,
  getActivePaymentProfileCookieKey
} from '~/domains/platform/lib/payment-profile-storage'

import { useGetPaymentProfiles } from './use-get-payment-profiles'

const listeners = new Set<() => void>()

function emit() {
  listeners.forEach(listener => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function readActivePaymentProfileId(): string {
  const fromCookie = isomorphicCookies.getAll()?.[getActivePaymentProfileCookieKey()]
  if (fromCookie) return fromCookie
  if (typeof window === 'undefined') return ''
  return Bucket.local.get(BUCKET_KEY) ?? ''
}

export function useActivePaymentProfile() {
  const activeProfileId = useSyncExternalStore(subscribe, readActivePaymentProfileId, () => '')

  const { deusExMachina } = useDeusExMachina()
  const stoneAccountId = deusExMachina?.entity?.stoneAccount?.id ?? ''
  // Resolve o objeto só quando há seleção — evita disparar o fetch da lista em toda página.
  const { data: paymentProfiles = [], isSuccess } = useGetPaymentProfiles({
    stoneAccountId,
    enabled: Boolean(activeProfileId)
  })

  const activeProfile = useMemo(
    () => paymentProfiles.find(profile => profile.id === activeProfileId),
    [paymentProfiles, activeProfileId]
  )

  const setActiveProfile = useCallback((id: string) => {
    Bucket.local.set(BUCKET_KEY, id)
    isomorphicCookies.set(getActivePaymentProfileCookieKey(), id, {
      domain: getCookieDomain(window.location.href),
      path: '/'
    })
    emit()
  }, [])

  const clear = useCallback(() => {
    Bucket.local.remove(BUCKET_KEY)
    isomorphicCookies.remove(getActivePaymentProfileCookieKey(), {
      domain: getCookieDomain(window.location.href)
    })
    emit()
  }, [])

  // Expurga o PP persistido que não pertence à sessão (cookie vazado entre ambientes ou órfão de logout falho),
  // forçando o Gate a reagir com seleção/auto-seleção. Só age com a lista carregada (nunca em loading/erro).
  useEffect(() => {
    if (!activeProfileId || !isSuccess) return
    const exists = paymentProfiles.some(profile => profile.id === activeProfileId)
    if (!exists) clear()
  }, [activeProfileId, isSuccess, paymentProfiles, clear])

  return { activeProfile, activeProfileId, setActiveProfile, clear }
}
